import React, { useEffect, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import MUIDataTable from "mui-datatables";

// MUI imports
import {
  Box,
  CircularProgress,
  ThemeProvider,
  Grid,
  Card,
  Typography,
  CardContent,
  Paper,
  Container,
  Tooltip,
  IconButton,
} from "@mui/material";

import Page from "../../../Components/Dashboard/Page";
import {
  ViewIcon,
  MuiDataTableTheme,
} from "../../../Components/MuiDataTableTheme/MuiDataTableTheme";
import { poppinsCustomTheme } from "../../../Styles/GlobalStyles/PoppinsTheme";
import LoadingOverlay from "react-loading-overlay-ts";
import Searchbox from "../../../Components/Dashboard/Searchbox";
import StatusGenerator from "../../../Components/StatusGenerator/StatusGenerator";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";

import {
  paFetchAllTrainers,
  updateCurrentTrainingCenterId,
  exportPhysicallyApprovedTrainers,
} from "../../../Actions";

const ColumnTextStyle = {
  textTransform: "capitalize",
  textAlign: "left",
  color: "#212b36",
  fontSize: "0.875rem",
  fontWeight: 600,
};

const DatatableTitle = ({ searchText, handleSearchChange }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={10}
    justifyContent="space-between"
  >
    <Typography sx={{ color: "#05004E", fontWeight: 600 }}>
      Accredited Training Centers
    </Typography>
    <Searchbox
      value={searchText}
      onChange={(event) => handleSearchChange(event.target.value)}
    />
  </Box>
);

export const DownloadIcon = () => {
  const paState = (state) => state.tregPhysicalApprovalReducer;

  const { exportingPaTrainers } = useSelector(paState);

  if (exportingPaTrainers) {
    return <CircularProgress size={20} />;
  } else {
    return <CloudDownloadOutlinedIcon />;
  }
};

const CustomTableColumnHeaders = ({ index, column }) => {
  const headStyle = {};

  if (column.label === "Name") {
    headStyle.paddingLeft = "24px";
  }
  return (
    <Typography style={headStyle} sx={ColumnTextStyle} key={index}>
      {column.label}
    </Typography>
  );
};

const paColumnOptions = {
  customHeadLabelRender: ({ index, ...column }) => {
    return <CustomTableColumnHeaders index={index} column={column} />;
  },
};

const INITIAL_STATE = {
  parsedListOfTrainers: [],
  pageNumber: 0,
  searchText: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "updateParsedListOfTrainers":
      return {
        ...state,
        parsedListOfTrainers: action.body,
      };

    case "changeTablePage":
      return {
        ...state,
        pageNumber: action.body,
      };

    case "handleSearchTextChange":
      return {
        ...state,
        searchText: action.body,
      };

    default:
      return state;
  }
};

const PhysicalApproval = () => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const paState = (state) => state.tregPhysicalApprovalReducer;

  const globalDispatch = useDispatch();
  const navigate = useNavigate();
  const {
    paAllTrainersList, 
    fetchingTrainers,
    exportingPaTrainers,
  } = useSelector(paState);

  function handleViewTrainingCenter(value) {
    globalDispatch(updateCurrentTrainingCenterId(value));
    navigate("/home/trainersmanagement/physicalApprovalReport");
  }

  function handleSearchTextChange(text) {
    console.log(text);
    dispatch({
      type: "handleSearchTextChange",
      body: text,
    });
  }

  useEffect(() => {
    globalDispatch(
      paFetchAllTrainers({
        pageNumber: state.pageNumber,
        search: state.searchText,
        FilterOptions: {
          IsVirtualApproval: false
        }
      })
    );
  }, [globalDispatch, state.pageNumber, state.searchText]);

  useEffect(() => {
    // Generate training center listing table data

    if (Array.isArray(paAllTrainersList)) {
      let parseOutput = [];

      paAllTrainersList.map((trainer) => {
        if (
          trainer.approvalStatus === "HeadQuatersApproved" ||
          trainer.approvalStatus === "PhysicalVerificationApproved"
        ) {
          //const trainerLocation = `${trainer.lga}, ${trainer.state} State`;
          var skillNames = "";
          trainer.skills.map((skill) => (skillNames += `${skill.name},`));

          if (skillNames.length > 0) {
            skillNames = skillNames.slice(0, skillNames.length - 1);
          } else {
            skillNames = "-";
          }

          // const ownerData = trainer.trainers.find(
          //   (trainer) => trainer.isOwner === true
          // );

          // let ownerName = "";
          // if (ownerData === undefined) {
          //   ownerName = "-";
          // } else {
          //   ownerName = `${ownerData.firstName} ${ownerData.middleName} ${ownerData.lastName}`;
          // }

          const tableModel = {
            skillCenterName: trainer.skillCenterName,
            // skillNames,
            // ownerName,
            centerPhoneNumber: trainer.centerPhoneNumber,
            centreEmail: trainer.centerEmail,
            registrationCode: trainer.registrationCode.toUpperCase(),
            state: trainer.state,
            lga: trainer.lga,
            approvalStatus: trainer.approvalStatus,
            id: trainer.id,
            //registrationCode: trainer.registrationCode.toUpperCase(),
            //location: trainerLocation,
          };

          parseOutput.push(tableModel);
        }
        return null;
      });

      dispatch({
        type: "updateParsedListOfTrainers",
        body: parseOutput,
      });
    }
  }, [paAllTrainersList]);

  const paColumns = [
    {
      name: "skillCenterName",
      label: "Training center name",
      options: {
        setCellProps: () => ({
          style: { minWidth: "130px", paddingLeft: "20px" },
        }),

        customHeadLabelRender: ({ index, ...column }) => {
          return (
            <Typography sx={ColumnTextStyle} key={index}>
              <span style={{ marginLeft: "20px" }}>{column.label}</span>
            </Typography>
          );
        },
        customBodyRender: (value) => {
          return value === undefined || value === "" || value === null
            ? "null"
            : value;
        },
        filter: false,
      },
    },
    // {
    //   name: "skillNames",
    //   label: "Skill Set",
    //   options: paColumnOptions,
    // },
    // {
    //   name: "ownerName",
    //   label: "Owner Name",
    //   options: paColumnOptions,
    // },
    {
      name: "centerPhoneNumber",
      label: "Phone Number",
      options: paColumnOptions,
    },
    {
      name: "centreEmail",
      label: "Email Address",
      options: paColumnOptions,
    },
    {
      name: "state",
      label: "state",
      options: paColumnOptions,
    },
    {
      name: "lga",
      label: "LGA",
      options: paColumnOptions,
    },
    {
      name: "approvalStatus",
      label: "Approval Status",
      options: {
        ...paColumnOptions,
        customBodyRender: (value, tableMeta) => {
          switch (value) {
            case "HeadQuatersApproved":
              return (
                <StatusGenerator
                  statusText="Pending Approval"
                  variant="yellowFilled"
                />
              );
            case "PhysicalVerificationApproved":
              return (
                <StatusGenerator statusText="Approved" variant="greenFilled" />
              );
            default:
              return "No status";
          }
        },
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        ...paColumnOptions,
        customBodyRender: (value, tableMeta) => {
          return (
            <Tooltip title={"View Training Center"}>
              <IconButton
                size="small"
                onClick={() => handleViewTrainingCenter(value)}
              >
                <ViewIcon icon="material-symbols:visibility-outline" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
  ];

  const paOptions = {
    filter: false,
    count: state.parsedListOfTrainers.length,
    disableRipple: true,
    elevation: 0,
    fixedHeader: true,
    viewColumns: false,
    print: false,
    responsive: "standard",
    rowsPerPage: 10,
    rowsPerPageOptions: [10],
    search: false,
    searchText: state.searchText,
    selectableRows: "none",
    serverSide: true,
    tableBodyHeight: "600px",
    tableBodyMaxHeight: "1000px",
    textLabels: {
      toolbar: {
        search: "Search",
        downloadCsv: "Download Excel Sheet",
      },
    },

    onTableChange: (action, tableState) => {
      // treat tableState.page as index
      // there's a useEffect watching getTrainersReportBody
      if (action === "changePage") {
        dispatch({
          type: "changeTablePage",
          body: tableState.page + 1,
        });
      }
    },
    onDownload: () => {
      if (!exportingPaTrainers) {
        const filter = {
          "FilterOptions.ApprovalStatus": 7,
        };
        globalDispatch(exportPhysicallyApprovedTrainers(filter));
      }
      return false;
    },
  };

  const tableComponents = {
    icons: {
      DownloadIcon,
    },
  };

  return (
    <ThemeProvider theme={poppinsCustomTheme}>
      <Page title="SAED | Trainers | Physical Approval">
        <Page>
          <Container maxWidth="xl">
            <Box sx={{ pb: 2 }}>
              <Typography
                variant="h5"
                style={{ fontWeight: 600, color: "#212B36" }}
              >
                Trainers Management &gt;{" "}
                <span style={{ fontSize: "smaller", fontWeight: 200 }}>
                  Physical Approval
                </span>
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box>
                  <Paper
                    sx={{
                      boxShadow: "0 1px 2px 0 rgb(145 158 171 / 24%)",
                      borderRadius: "20px",
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{ padding: "0px !important", borderRadius: "20px" }}
                    >
                      <CardContent sx={{ padding: "0px !important" }}>
                        <ThemeProvider theme={MuiDataTableTheme}>
                          <LoadingOverlay
                            active={fetchingTrainers}
                            styles={{
                              overlay: (base) => ({
                                ...base,
                                background: "rgba(0, 0, 0, 0.15)",
                              }),
                            }}
                            spinner
                            text="Fetching Data for You..."
                          >
                            <div id="partners-datatable">
                              <MUIDataTable
                                title={
                                  <DatatableTitle
                                    searchText={state.searchText}
                                    handleSearchChange={handleSearchTextChange}
                                  />
                                }
                                columns={paColumns}
                                options={paOptions}
                                components={tableComponents}
                                style={{ transition: "0.7s" }}
                                data={state.parsedListOfTrainers}
                              />
                            </div>
                          </LoadingOverlay>
                        </ThemeProvider>
                      </CardContent>
                    </Card>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Page>
      </Page>
    </ThemeProvider>
  );
};

export default PhysicalApproval;
